import type { Variants } from 'framer-motion';

/**
 * Standard easing curves
 */
export const easing = {
  out: [0, 0, 0.2, 1] as const,
  in: [0.4, 0, 1, 1] as const,
  inOut: [0.4, 0, 0.2, 1] as const,
  elegant: [0.22, 1, 0.36, 1] as const,
};

/**
 * Fade in from bottom
 */
export const fadeInUp: Variants = {
  initial: {
    opacity: 0,
    y: 40,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: easing.elegant,
    },
  },
};

/**
 * Fade in from bottom with viewport trigger
 */
export const fadeInUpOnScroll: Variants = {
  initial: {
    opacity: 0,
    y: 40,
  },
  whileInView: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: easing.elegant,
    },
  },
};

/**
 * Simple fade in
 */
export const fadeIn: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: easing.out,
    },
  },
};

/**
 * Scale up on hover
 */
export const scaleOnHover: Variants = {
  initial: {
    scale: 1,
  },
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.3,
      ease: easing.out,
    },
  },
};

/**
 * Staggered container for children
 */
export const staggerContainer: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

/**
 * Staggered child item
 */
export const staggerItem: Variants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: easing.elegant,
    },
  },
};

/**
 * Slide in from left
 */
export const slideInLeft: Variants = {
  initial: {
    opacity: 0,
    x: -60,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: easing.elegant,
    },
  },
};

/**
 * Slide in from right
 */
export const slideInRight: Variants = {
  initial: {
    opacity: 0,
    x: 60,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: easing.elegant,
    },
  },
};

/**
 * Page transition
 */
export const pageTransition: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: easing.out,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: easing.in,
    },
  },
};

/**
 * Hero text reveal (line by line)
 */
export const heroTextReveal: Variants = {
  initial: {
    opacity: 0,
    y: 60,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: easing.elegant,
    },
  },
};

/**
 * Image reveal with scale
 */
export const imageReveal: Variants = {
  initial: {
    opacity: 0,
    scale: 1.1,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: easing.elegant,
    },
  },
};

/**
 * Viewport settings for scroll animations
 */
export const viewportSettings = {
  once: true,
  amount: 0.3,
  margin: '0px 0px -100px 0px',
};
